/**
 * Category Presentation Configuration
 * 
 * This module contains ALL presentation-related data for categories.
 * Icons, colors, display names, descriptions - everything visual.
 * 
 * WHY: Separates business data (stored in DB) from presentation (UI concern)
 * 
 * Benefits:
 * - Change icons/colors without database migrations
 * - Support multiple themes (light/dark)
 * - Easy A/B testing
 * - Localization support
 * - Icon library swapping
 * - Performance (no DB queries for UI data)
 */

export const SYSTEM_CATEGORY_TYPES = [
    "food",
    "transport",
    "housing",
    "shopping",
    "health",
    "entertainment",
    "travel",
    "work",
    "education",
    "subscriptions",
    "other",
] as const;

export type CategoryType = (typeof SYSTEM_CATEGORY_TYPES)[number];

export interface CategoryPresentation {
    type: CategoryType;
    defaultName: string;
    icon: string;
    color: string;
    darkColor: string;
    description: string;
}

/**
 * Master presentation configuration for all category types
 * This is the SINGLE SOURCE OF TRUTH for how categories look
 */
export const CATEGORY_PRESENTATION: Record<
    CategoryType,
    CategoryPresentation
> = {
    food: {
        type: "food",
        defaultName: "Food & Dining",
        icon: "🍽️",
        color: "#FF6B6B",
        darkColor: "#FF8585",
        description: "Restaurants, groceries, dining",
    },
    transport: {
        type: "transport",
        defaultName: "Transportation",
        icon: "🚗",
        color: "#4ECDC4",
        darkColor: "#6EDFD7",
        description: "Commute, fuel, parking, rideshare",
    },
    housing: {
        type: "housing",
        defaultName: "Housing",
        icon: "🏠",
        color: "#95E1D3",
        darkColor: "#A8E8DD",
        description: "Rent, utilities, maintenance, internet",
    },
    shopping: {
        type: "shopping",
        defaultName: "Shopping",
        icon: "🛒",
        color: "#FFB6A3",
        darkColor: "#FFC4B4",
        description: "Clothing, household items, general goods",
    },
    health: {
        type: "health",
        defaultName: "Health & Wellness",
        icon: "💊",
        color: "#B693E8",
        darkColor: "#C5A8F0",
        description: "Medical, pharmacy, fitness",
    },
    entertainment: {
        type: "entertainment",
        defaultName: "Entertainment",
        icon: "🎮",
        color: "#FF94C2",
        darkColor: "#FFA8D0",
        description: "Media, hobbies, recreation",
    },
    travel: {
        type: "travel",
        defaultName: "Travel",
        icon: "✈️",
        color: "#87CEEB",
        darkColor: "#9CD9F5",
        description: "Vacation, accommodation, flights",
    },
    work: {
        type: "work",
        defaultName: "Work",
        icon: "💼",
        color: "#6C5CE7",
        darkColor: "#8171ED",
        description: "Office supplies, professional services",
    },
    education: {
        type: "education",
        defaultName: "Education",
        icon: "🎓",
        color: "#FFD93D",
        darkColor: "#FFE05C",
        description: "Courses, books, learning materials",
    },
    subscriptions: {
        type: "subscriptions",
        defaultName: "Subscriptions",
        icon: "📱",
        color: "#95E77D",
        darkColor: "#A8EE92",
        description: "Recurring services and memberships",
    },
    other: {
        type: "other",
        defaultName: "Other",
        icon: "📊",
        color: "#B8B8B8",
        darkColor: "#C8C8C8",
        description: "Miscellaneous expenses",
    },
};

/**
 * Get presentation configuration for a category type
 * @param type - Category type identifier
 * @param theme - UI theme (light or dark)
 * @returns Presentation configuration with theme-aware color
 */
export function getCategoryPresentation(
    type: CategoryType,
    theme: "light" | "dark" = "light",
): CategoryPresentation {
    const presentation = CATEGORY_PRESENTATION[type];
    return {
        ...presentation,
        color: theme === "dark" ? presentation.darkColor : presentation.color,
    };
}

/**
 * Get the default name for a category type
 * @param type - Category type identifier
 * @returns Default display name
 */
export function getCategoryDefaultName(type: CategoryType): string {
    return CATEGORY_PRESENTATION[type].defaultName;
}

/**
 * Get the icon for a category type
 * @param type - Category type identifier
 * @returns Icon string (emoji or identifier)
 */
export function getCategoryIcon(type: CategoryType): string {
    return CATEGORY_PRESENTATION[type].icon;
}

/**
 * Get the color for a category type
 * @param type - Category type identifier
 * @param theme - UI theme
 * @returns Hex color code
 */
export function getCategoryColor(
    type: CategoryType,
    theme: "light" | "dark" = "light",
): string {
    const presentation = CATEGORY_PRESENTATION[type];
    return theme === "dark" ? presentation.darkColor : presentation.color;
}

/**
 * Check if a string is a valid category type
 * @param type - String to check
 * @returns True if valid category type
 */
export function isValidCategoryType(type: string): type is CategoryType {
    return SYSTEM_CATEGORY_TYPES.includes(type as CategoryType);
}

/**
 * Get all category types
 * @returns Array of all category type identifiers
 */
export function getAllCategoryTypes(): CategoryType[] {
    return [...SYSTEM_CATEGORY_TYPES];
}
